import React, { useState } from "react";
import { Eye, EyeOff } from "lucide-react";

interface CheckoutRegistrationModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSuccess: () => void;
  paymentInfo: {
    paymentId: string;
    courseTitle: string;
    amount: number;
    paymentType: string;
    level?: string;
  };
}

const CheckoutRegistrationModal: React.FC<CheckoutRegistrationModalProps> = ({
  isOpen,
  onClose,
  onSuccess,
  paymentInfo,
}) => {
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    whatsappNumber: "",
    aimInGermany: "",
    lastDegree: "",
    germanLanguageLevel: "",
    fathersName: "",
    mothersName: "",
    dateOfBirth: "",
    address: "",
    password: "",
    confirmPassword: "",
  });
  const [error, setError] = useState("");
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);
  const [showSuccessModal, setShowSuccessModal] = useState(false);

  const handleChange = (
    e: React.ChangeEvent<HTMLInputElement | HTMLSelectElement>
  ) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
    setError("");
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError("");

    // Validation
    if (
      !formData.firstName ||
      !formData.lastName ||
      !formData.email ||
      !formData.whatsappNumber ||
      !formData.aimInGermany ||
      !formData.lastDegree ||
      !formData.germanLanguageLevel ||
      !formData.fathersName ||
      !formData.mothersName ||
      !formData.dateOfBirth ||
      !formData.address ||
      !formData.password ||
      !formData.confirmPassword
    ) {
      setError("🚫 Please fill out all fields.");
      return;
    }

    if (formData.password !== formData.confirmPassword) {
      setError("🚫 Passwords do not match.");
      return;
    }

    if (formData.password.length < 8) {
      setError("🚫 Password must be at least 8 characters.");
      return;
    }

    setLoading(true);

    try {
      // Register without token (checkout flow)
      const response = await fetch(
        `${process.env.REACT_APP_API_BASE_URL}/api/auth/register`,
        {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          credentials: "include",
          body: JSON.stringify({
            firstName: formData.firstName,
            lastName: formData.lastName,
            email: formData.email,
            whatsappNumber: formData.whatsappNumber,
            aimInGermany: formData.aimInGermany,
            lastDegree: formData.lastDegree,
            germanLanguageLevel: formData.germanLanguageLevel,
            fathersName: formData.fathersName,
            mothersName: formData.mothersName,
            dateOfBirth: formData.dateOfBirth,
            address: formData.address,
            password: formData.password,
            role: "STUDENT",
            isActiveStudent: false,
          }),
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => null);
        const errorMessage =
          errorData?.error || errorData?.message || "Registration failed";
        throw new Error(errorMessage);
      }

      const result = await response.json();
      console.log("Registration successful:", result);

      // Extract student ID from response
      // The response might contain studentId, id, or be in a nested structure
      const studentId = result.studentId || result.id;

      // Link payment to student
      if (studentId) {
        try {
          const linkResponse = await fetch(
            `${process.env.REACT_APP_API_BASE_URL}/api/payments/${paymentInfo.paymentId}/link-student`,
            {
              method: "POST",
              headers: { "Content-Type": "application/json" },
              credentials: "include",
              body: JSON.stringify({
                studentId: studentId,
              }),
            }
          );

          if (!linkResponse.ok) {
            console.error("Failed to link payment to student");
          } else {
            console.log("Payment linked successfully");
          }
        } catch (linkError) {
          console.error("Error linking payment:", linkError);
          // Continue to show success modal even if linking fails
        }
      }

      // Reset loading state and show success modal
      setLoading(false);
      setShowSuccessModal(true);
      console.log("Success modal should be showing now");
    } catch (err: any) {
      console.error("Registration error:", err);
      setError(
        err.message.startsWith("🚫") ? err.message : `🚫 ${err.message}`
      );
      setLoading(false);
    }
  };

  const handleLoginClick = () => {
    // Store payment info for after login
    sessionStorage.setItem("pendingPaymentId", paymentInfo.paymentId);
    sessionStorage.setItem(
      "pendingPaymentCourseTitle",
      paymentInfo.courseTitle
    );
    sessionStorage.setItem("returnToCheckout", "true");

    onClose();
    // Navigate to login
    window.location.href = "/login";
  };

  const handleSuccessLoginRedirect = () => {
    setShowSuccessModal(false);
    onClose();
    // Store payment info and redirect to login
    sessionStorage.setItem("pendingPaymentId", paymentInfo.paymentId);
    sessionStorage.setItem(
      "pendingPaymentCourseTitle",
      paymentInfo.courseTitle
    );
    window.location.href = "/login";
  };

  if (!isOpen) return null;

  // ✅ Success Modal - Rendered separately with highest z-index
  if (showSuccessModal) {
    return (
      <div className="fixed inset-0 bg-black/60 backdrop-blur-sm flex items-center justify-center z-[99999] p-4">
        <div className="bg-white rounded-2xl shadow-2xl max-w-md w-full mx-4 animate-in fade-in zoom-in duration-300">
          <div className="p-8 text-center">
            {/* Success Icon */}
            <div className="mb-6 relative">
              <div className="text-8xl animate-bounce">🎉</div>
              <div className="absolute inset-0 flex items-center justify-center">
                <div className="w-24 h-24 bg-green-100 rounded-full opacity-20 animate-ping"></div>
              </div>
            </div>

            {/* Success Message */}
            <h2 className="text-3xl font-bold mb-4 text-green-600">
              Registration Successful!
            </h2>
            <p className="mb-8 text-gray-600 text-base leading-relaxed">
              Your account has been created successfully. Please log in to
              continue with your course enrollment.
            </p>

            {/* Action Button */}
            <button
              onClick={handleSuccessLoginRedirect}
              className="w-full bg-gradient-to-r from-[#009fe3] to-[#007cb5] hover:from-[#007cb5] hover:to-[#006699] text-white px-6 py-4 rounded-lg font-semibold transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              Go to Login
            </button>
          </div>
        </div>
      </div>
    );
  }

  // ✅ Registration Form Modal - Only rendered when success modal is NOT showing
  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-[9999] overflow-y-auto">
      <div className="min-h-full flex items-start justify-center p-3 sm:p-4 sm:items-center">
      <div className="bg-white rounded-2xl shadow-2xl max-w-2xl w-full my-4 sm:my-8">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#009fe3] to-[#007cb5] p-4 sm:p-6 rounded-t-2xl">
          <div className="flex items-center justify-between">
            <div>
              <h2 className="text-xl sm:text-2xl font-bold text-white mb-1">
                Create Your Account
              </h2>
              <p className="text-white/90 text-sm">
                Register to complete your enrollment
              </p>
            </div>
            <button
              onClick={onClose}
              disabled={loading}
              className="text-white/80 hover:text-white transition p-2 hover:bg-white/10 rounded-lg disabled:opacity-50"
            >
              <svg
                className="w-6 h-6"
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              </svg>
            </button>
          </div>
        </div>

        {/* Course Info */}
        <div className="bg-blue-50 border-l-4 border-[#009fe3] p-4">
          <div className="flex items-start gap-3">
            <svg
              className="w-6 h-6 text-[#009fe3] flex-shrink-0 mt-0.5"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth={2}
                d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z"
              />
            </svg>
            <div className="flex-1">
              <p className="text-sm font-semibold text-gray-900 mb-1">
                Enrolling in: {paymentInfo.courseTitle}
              </p>
              <p className="text-sm text-gray-600">
                Amount: ৳{paymentInfo.amount.toLocaleString()}
              </p>
            </div>
          </div>
        </div>

        {/* Form Content */}
        <div className="p-4 sm:p-6">
          {/* Error Message */}
          {error && (
            <div className="mb-6 p-4 bg-red-50 border-l-4 border-red-500 rounded-r-lg">
              <div className="flex items-start gap-3">
                <svg
                  className="w-5 h-5 text-red-500 flex-shrink-0 mt-0.5"
                  fill="currentColor"
                  viewBox="0 0 20 20"
                >
                  <path
                    fillRule="evenodd"
                    d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z"
                    clipRule="evenodd"
                  />
                </svg>
                <p className="text-sm text-red-700 font-medium">{error}</p>
              </div>
            </div>
          )}

          <form onSubmit={handleSubmit}>
            {/* Form Fields */}
            <div className="space-y-4">
              {/* Row 1: First Name & Last Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    First Name *
                  </label>
                  <input
                    type="text"
                    name="firstName"
                    required
                    value={formData.firstName}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter first name"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Name *
                  </label>
                  <input
                    type="text"
                    name="lastName"
                    required
                    value={formData.lastName}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter last name"
                  />
                </div>
              </div>

              {/* Row 2: Email & WhatsApp */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Email Address *
                  </label>
                  <input
                    type="email"
                    name="email"
                    required
                    value={formData.email}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter email address"
                  />
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    WhatsApp Number *
                  </label>
                  <input
                    type="text"
                    name="whatsappNumber"
                    required
                    value={formData.whatsappNumber}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter WhatsApp number"
                  />
                </div>
              </div>

              {/* Row 3: Aim in Germany & Last Degree */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Aim in Germany *
                  </label>
                  <select
                    name="aimInGermany"
                    required
                    value={formData.aimInGermany}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100 text-gray-700"
                  >
                    <option value="">Select your aim</option>
                    <option value="Ausbildung">Ausbildung</option>
                    <option value="Bachelor">Bachelor</option>
                    <option value="Masters">Masters</option>
                  </select>
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Last Degree *
                  </label>
                  <select
                    name="lastDegree"
                    required
                    value={formData.lastDegree}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100 text-gray-700"
                  >
                    <option value="">Select your last degree</option>
                    <option value="SSC">SSC</option>
                    <option value="HSC">HSC</option>
                    <option value="Diploma">Diploma</option>
                    <option value="Bachelor">Bachelor</option>
                    <option value="Masters">Masters</option>
                  </select>
                </div>
              </div>

              {/* Row 4: German Language Level (Full Width) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  German Language Level *
                </label>
                <select
                  name="germanLanguageLevel"
                  required
                  value={formData.germanLanguageLevel}
                  onChange={handleChange}
                  disabled={loading}
                  className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100 text-gray-700"
                >
                  <option value="">Select your level</option>
                  <option value="None">None</option>
                  <option value="A1">A1</option>
                  <option value="A2">A2</option>
                  <option value="B1">B1</option>
                  <option value="B2">B2</option>
                  <option value="C1">C1</option>
                </select>
              </div>

              {/* Row 5: Father's Name & Mother's Name */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Father's Name *
                  </label>
                  <input
                    type="text"
                    name="fathersName"
                    required
                    value={formData.fathersName}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter father's name"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Mother's Name *
                  </label>
                  <input
                    type="text"
                    name="mothersName"
                    required
                    value={formData.mothersName}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter mother's name"
                  />
                </div>
              </div>

              {/* Row 6: Date of Birth & Address */}
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Date of Birth *
                  </label>
                  <input
                    type="date"
                    name="dateOfBirth"
                    required
                    value={formData.dateOfBirth}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-1">
                    Complete Address *
                  </label>
                  <input
                    type="text"
                    name="address"
                    required
                    value={formData.address}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Enter complete address"
                  />
                </div>
              </div>

              {/* Password (Full Width) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Password *
                </label>
                <div className="relative">
                  <input
                    type={showPassword ? "text" : "password"}
                    name="password"
                    required
                    value={formData.password}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Create a password (min. 8 characters)"
                  />
                  <button
                    type="button"
                    onClick={() => setShowPassword(!showPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition"
                  >
                    {showPassword ? <EyeOff size={20} /> : <Eye size={20} />}
                  </button>
                </div>
              </div>

              {/* Confirm Password (Full Width) */}
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">
                  Confirm Password *
                </label>
                <div className="relative">
                  <input
                    type={showConfirmPassword ? "text" : "password"}
                    name="confirmPassword"
                    required
                    value={formData.confirmPassword}
                    onChange={handleChange}
                    disabled={loading}
                    className="w-full px-4 py-3 pr-12 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#009fe3] focus:border-transparent outline-none transition disabled:bg-gray-100"
                    placeholder="Re-enter your password"
                  />
                  <button
                    type="button"
                    onClick={() => setShowConfirmPassword(!showConfirmPassword)}
                    className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-500 hover:text-gray-700 transition"
                  >
                    {showConfirmPassword ? (
                      <EyeOff size={20} />
                    ) : (
                      <Eye size={20} />
                    )}
                  </button>
                </div>
                <p className="text-xs text-gray-500 mt-1">
                  Minimum 8 characters
                </p>
              </div>
            </div>

            {/* Submit Button */}
            <button
              type="submit"
              disabled={loading}
              className="w-full mt-6 py-4 bg-gradient-to-r from-[#009fe3] to-[#007cb5] text-white rounded-lg hover:from-[#007cb5] hover:to-[#006699] font-semibold disabled:opacity-50 disabled:cursor-not-allowed transition-all shadow-lg hover:shadow-xl transform hover:-translate-y-0.5"
            >
              {loading ? (
                <span className="flex items-center justify-center gap-2">
                  <svg
                    className="animate-spin h-5 w-5"
                    xmlns="http://www.w3.org/2000/svg"
                    fill="none"
                    viewBox="0 0 24 24"
                  >
                    <circle
                      className="opacity-25"
                      cx="12"
                      cy="12"
                      r="10"
                      stroke="currentColor"
                      strokeWidth="4"
                    ></circle>
                    <path
                      className="opacity-75"
                      fill="currentColor"
                      d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"
                    ></path>
                  </svg>
                  Creating Account...
                </span>
              ) : (
                "Create Account"
              )}
            </button>
          </form>

          {/* Login Option */}
          <div className="mt-6 text-center border-t pt-6">
            <p className="text-sm text-gray-600">
              Already have an account?{" "}
              <button
                type="button"
                onClick={handleLoginClick}
                className="text-[#009fe3] hover:underline font-semibold transition"
              >
                Login here
              </button>
            </p>
          </div>
        </div>
      </div>
      </div>
    </div>
  );
};

export default CheckoutRegistrationModal;
